<template>
  <div :class="[bemClass()]">
    <slot name="decrement"
          :decrement="decrement"
          :button-classes="decrementClasses"
          :attrs="decrementAttrs"
    >
      <sel-button :class="decrementClasses"
                  modifier="number-control"
                  type="button"
                  aria-label="Decrement"
                  v-bind="decrementAttrs"
                  @click.prevent="decrement"
      >-
      </sel-button>
    </slot>
    <sel-text-input v-if="showInput"
                    v-model="inputValue"
                    :class="bemClass('input')"
                    :lazy="true"
                    :attrs="{min, max, type: 'number'}"
                    modifier="number-control"
    ></sel-text-input>
    <span v-else v-html="modelValue" :class="bemClass('value')"></span>
    <slot name="increment"
          :increment="increment"
          :button-classes="incrementClasses"
          :attrs="incrementAttrs"
    >
      <sel-button :class="incrementClasses"
                  modifier="number-control"
                  type="button"
                  aria-label="Increment"
                  v-bind="incrementAttrs"
                  @click.prevent="increment"
      >+
      </sel-button>
    </slot>
  </div>
  <transition name="fade">
    <slot v-if="isMaxExceeded" name="exceeded" v-bind="{ max }"></slot>
  </transition>
</template>

<script>
import { computed, toRefs, ref } from 'vue'
import useBem from '../../compositions/bem'

export default {
  name: 'SelNumberControl',
  props: {
    modelValue: {
      type: Number,
      default: 0
    },
    name: {
      type: String,
      default: ''
    },
    max: {
      type: Number,
      default: NaN
    },
    min: {
      type: Number,
      default: 0
    },
    showInput: {
      type: Boolean,
      default: true
    },
    modifier: {
      type: String,
      default: ''
    },
    maxExceededTimeout: {
      type: Number,
      default: 3000
    }
  },
  setup (props, { emit }) {
    const { modifier, modelValue, max, min } = toRefs(props)
    const isMaxExceeded = ref(false)
    const inputValue = computed({
      get () {
        return modelValue.value
      },
      set (value) {
        // new value compared to min
        const newValue = Math.max(
          props.min,
          value
        )

        if (!isNaN(max.value) && newValue >= max.value) {
          isMaxExceeded.value = true
          setTimeout(() => {
            isMaxExceeded.value = false
          }, props.maxExceededTimeout)
        }

        // new value compared to max
        const inputValue = isNaN(max.value)
          ? newValue
          : Math.min(max.value, newValue)

        emit('update:modelValue', inputValue)
      }
    })

    const { bemClass } = useBem('sel-number-control', modifier)

    const increment = () => {
      inputValue.value += 1
    }

    const incrementClasses = computed(() => [
      bemClass('button'),
      bemClass('increment')
    ])

    const incrementAttrs = computed(() => ({
      disabled: !isNaN(inputValue.value) && inputValue.value >= max.value
    }))

    const decrement = () => {
      inputValue.value -= 1
    }

    const decrementClasses = computed(() => [
      bemClass('button'),
      bemClass('decrement')
    ])

    const decrementAttrs = computed(() => ({
      disabled: inputValue.value <= min.value
    }))

    return {
      bemClass,
      inputValue,
      increment,
      incrementClasses,
      incrementAttrs,
      decrement,
      decrementClasses,
      decrementAttrs,
      isMaxExceeded
    }
  }
}
</script>

<style scoped>
.sel-number-control {
  display: flex;
}

.sel-number-control button {
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
}
</style>
